/**
 * React Query Hooks for CoinGecko API
 *
 * Provides hooks for fetching crypto data from CoinGecko with
 * automatic caching, retry logic, and rate limiting.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CoinGeckoAPI } from '../../services/api';
import { queryKeys, staleTimeConfig } from '../../config/queryClient';
import type { Prices, Coin, CoinDetails } from '../../types';

/**
 * Query keys for CoinGecko API
 */
export const coinGeckoKeys = {
  all: ['coingecko'] as const,
  prices: (coinIds: string[]) => [...coinGeckoKeys.all, 'prices', coinIds.sort().join(',')] as const,
  search: (query: string) => [...coinGeckoKeys.all, 'search', query] as const,
  coinDetails: (coinId: string) => [...coinGeckoKeys.all, 'details', coinId] as const,
  chartData: (coinId: string, days: number) => [...coinGeckoKeys.all, 'chart', coinId, days] as const,
  coinsList: () => [...coinGeckoKeys.all, 'list'] as const,
};

/**
 * Fetch prices for multiple coins
 *
 * @param coinIds - Array of CoinGecko coin IDs
 * @returns Query with prices data
 *
 * @example
 * const { data: prices, isLoading } = useCoinPrices(['bitcoin', 'ethereum']);
 * // prices = { bitcoin: { usd: 68000, usd_24h_change: 2.5 }, ... }
 */
export function useCoinPrices(
  coinIds: string[],
  options?: { enabled?: boolean; refetchInterval?: number | false }
) {
  return useQuery({
    queryKey: coinGeckoKeys.prices(coinIds),
    queryFn: async () => {
      if (!coinIds || coinIds.length === 0) return {};
      return await CoinGeckoAPI.fetchPrices(coinIds);
    },
    staleTime: staleTimeConfig.frequent, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    enabled: options?.enabled !== false && coinIds.length > 0,
    // Don't retry as much for price fetching (CoinGecko rate limits)
    retry: 2,
    // Allow custom refetch interval (default: none)
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Search coins by query
 *
 * @param query - Search query
 * @param options - Query options
 * @returns Query with search results
 *
 * @example
 * const { data, isLoading } = useSearchCoins('bitcoin');
 * // data = { coins: [{ id: 'bitcoin', name: 'Bitcoin', ... }] }
 */
export function useSearchCoins(query: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: coinGeckoKeys.search(query),
    queryFn: async () => {
      if (!query || query.length < 2) return { coins: [] };
      return await CoinGeckoAPI.searchCoins(query);
    },
    staleTime: staleTimeConfig.slow, // 15 minutes (search results don't change often)
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    enabled: options?.enabled !== false && query.length >= 2,
    retry: 1, // Only retry once for searches
  });
}

/**
 * Get coin details
 *
 * @param coinId - CoinGecko coin ID
 * @param options - Query options
 * @returns Query with coin details
 *
 * @example
 * const { data: details, isLoading } = useCoinDetails('bitcoin');
 * // details = { id: 'bitcoin', name: 'Bitcoin', description: { en: '...' }, ... }
 */
export function useCoinDetails(coinId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: coinGeckoKeys.coinDetails(coinId || ''),
    queryFn: async () => {
      if (!coinId) throw new Error('Coin ID is required');
      return await CoinGeckoAPI.getCoinDetails(coinId);
    },
    staleTime: staleTimeConfig.slow, // 15 minutes (details don't change often)
    gcTime: 60 * 60 * 1000, // 1 hour cache
    enabled: options?.enabled !== false && !!coinId,
    retry: 2,
  });
}

/**
 * Get historical chart data
 *
 * @param coinId - CoinGecko coin ID
 * @param days - Number of days of history (default: 7)
 * @param options - Query options
 * @returns Query with chart data
 *
 * @example
 * const { data, isLoading } = useChartData('bitcoin', 30);
 * // data = { prices: [[timestamp, price], ...], market_caps: [...], total_volumes: [...] }
 */
export function useChartData(
  coinId: string | undefined,
  days: number = 7,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: coinGeckoKeys.chartData(coinId || '', days),
    queryFn: async () => {
      if (!coinId) throw new Error('Coin ID is required');
      return await CoinGeckoAPI.getChartData(coinId, days);
    },
    staleTime: days <= 1 ? staleTimeConfig.frequent : staleTimeConfig.slow, // 2 min for 1 day, 15 min for longer periods
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    enabled: options?.enabled !== false && !!coinId,
    retry: 2,
  });
}

/**
 * Get all coins list with intelligent caching
 *
 * This uses React Query's cache plus localStorage fallback for 24h persistence.
 * Perfect for autocomplete/search features.
 *
 * @param options - Query options
 * @returns Query with coins list
 *
 * @example
 * const { data: coins, isLoading } = useCoinsList();
 * // coins = [{ id: 'bitcoin', symbol: 'btc', name: 'Bitcoin' }, ...]
 */
export function useCoinsList(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: coinGeckoKeys.coinsList(),
    queryFn: async () => {
      // This already has 24h localStorage caching built-in
      return await CoinGeckoAPI.getCachedCoinsList();
    },
    staleTime: 60 * 60 * 1000, // 1 hour (data cached in localStorage for 24h anyway)
    gcTime: 24 * 60 * 60 * 1000, // 24 hours cache in React Query
    enabled: options?.enabled !== false,
    retry: 1,
    // This data is quite stable, so we don't need aggressive refetching
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

/**
 * Prefetch coin prices
 *
 * Useful for preloading data before user navigates to a page.
 *
 * @example
 * const prefetchPrices = usePrefetchCoinPrices();
 * // On hover or before navigation:
 * prefetchPrices(['bitcoin', 'ethereum']);
 */
export function usePrefetchCoinPrices() {
  const queryClient = useQueryClient();

  return async (coinIds: string[]) => {
    await queryClient.prefetchQuery({
      queryKey: coinGeckoKeys.prices(coinIds),
      queryFn: async () => {
        if (!coinIds || coinIds.length === 0) return {};
        return await CoinGeckoAPI.fetchPrices(coinIds);
      },
      staleTime: staleTimeConfig.frequent,
    });
  };
}

/**
 * Invalidate all CoinGecko queries
 *
 * Useful for manual refresh or after user action that might affect prices.
 *
 * @example
 * const invalidateCoingecko = useInvalidateCoinGecko();
 * // On refresh button click:
 * invalidateCoingecko();
 */
export function useInvalidateCoinGecko() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: coinGeckoKeys.all });
  };
}

/**
 * Get cached prices without refetching
 *
 * Useful when you need prices but don't want to trigger a new API call.
 *
 * @param coinIds - Array of coin IDs
 * @returns Cached prices or undefined
 *
 * @example
 * const getCachedPrices = useGetCachedPrices();
 * const prices = getCachedPrices(['bitcoin', 'ethereum']);
 */
export function useGetCachedPrices() {
  const queryClient = useQueryClient();

  return (coinIds: string[]): Prices | undefined => {
    return queryClient.getQueryData(coinGeckoKeys.prices(coinIds));
  };
}
